/* Aperçu partagé : le nom saisi (ou reçu par le lien du mail) et le logo joint suivent le visiteur.
   Le nom reste dans le navigateur (sessionStorage) : il n'est envoyé à aucun outil de mesure.
   Le logo reste en mémoire le temps de la page ; il n'est téléversé qu'à l'envoi du formulaire. */
(() => {
  if (window.adcGarment) return;
  const cfg = Object.assign({ max: 28, split: 11, upper: true }, window.adcConfig || {});
  const PLACEHOLDER = 'VOTRE ENTREPRISE';
  const LOGO_MAX = 10 * 1024 * 1024;
  const store = {
    get(k) { try { return sessionStorage.getItem(k) || ''; } catch (e) { return (window.__adcMem || {})[k] || ''; } },
    set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) { (window.__adcMem = window.__adcMem || {})[k] = v; } },
  };

  // Pas d'espace en tête, espaces multiples réduits, majuscules si le réglage le demande, longueur limitée.
  const clean = (raw) => {
    let v = String(raw || '').normalize('NFC').replace(/\s+/g, ' ').replace(/^ /, '');
    if (cfg.upper) v = v.toLocaleUpperCase('fr-FR');
    return v.slice(0, cfg.max);
  };
  // Remet la valeur nettoyée dans un champ en gardant le curseur à sa place.
  const writeBack = (input, v) => {
    if (v === input.value) return;
    const pos = input.selectionStart;
    const delta = input.value.length - v.length;
    input.value = v;
    try { const p = Math.max(0, (pos || 0) - Math.max(0, delta)); input.setSelectionRange(p, p); } catch (e) {}
  };
  const hexRgb = (c) => {
    let h = String(c || '').replace('#', '');
    if (h.length === 3) h = h.split('').map((x) => x + x).join('');
    const n = parseInt(h, 16);
    return Number.isNaN(n) ? [59, 62, 67] : [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  };
  const mix = (c, t, a) => {
    const A = hexRgb(c), B = hexRgb(t);
    return '#' + A.map((v, i) => Math.round(v + (B[i] - v) * a).toString(16).padStart(2, '0')).join('');
  };
  const isDark = (c) => { const [r, g, b] = hexRgb(c); return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 < 0.5; };
  const splitName = (text) => {
    if (text.length <= cfg.split || text.indexOf(' ') === -1) return [text, ''];
    const mid = text.length / 2;
    let best = -1;
    for (let i = 0; i < text.length; i++) {
      if (text[i] === ' ' && (best < 0 || Math.abs(i - mid) < Math.abs(best - mid))) best = i;
    }
    return best > 0 ? [text.slice(0, best), text.slice(best + 1)] : [text, ''];
  };

  const paintName = (g, name) => {
    const emb = g.querySelector('[data-adc-emb]');
    if (!emb) return;
    const value = (name || '').trim();
    const text = value || PLACEHOLDER;
    const [cx, cy, w, fmax] = (g.dataset.zone || '0,0,40,9').split(',').map(Number);
    const [l1, l2] = splitName(text);
    const longest = Math.max(l1.length, l2.length, 1);
    const fs = Math.min(fmax, w / (0.62 * longest)) * 0.62;
    const t1 = emb.querySelector('[data-adc-l1]');
    const t2 = emb.querySelector('[data-adc-l2]');
    emb.classList.toggle('is-placeholder', !value);
    t1.textContent = l1;
    t1.setAttribute('x', cx);
    t1.setAttribute('y', l2 ? cy - 0.225 * fs : cy + 0.35 * fs);
    t1.style.fontSize = fs + 'px';
    t2.textContent = l2;
    t2.setAttribute('x', cx);
    t2.setAttribute('y', cy + 0.925 * fs);
    t2.style.fontSize = fs + 'px';
  };
  const paintColor = (g, hex) => {
    if (!hex) return;
    const dark = isDark(hex);
    g.style.setProperty('--g', hex);
    g.style.setProperty('--line', mix(hex, '#0a0b0c', dark ? 0.55 : 0.7));
    g.style.setProperty('--seam', dark ? mix(hex, '#ffffff', 0.42) : mix(hex, '#000000', 0.36));
    g.style.setProperty('--shade', mix(hex, '#000000', 0.3));
    g.style.setProperty('--rib', mix(hex, '#000000', dark ? 0.38 : 0.2));
    g.toggleAttribute('data-dark', dark);
  };
  const paintThread = (g, hex) => { if (hex) g.style.setProperty('--adc-thread', hex); };
  // Logo : image affichée si le fichier est une image, cadre « VOTRE LOGO » sinon (PDF, AI…).
  const paintLogo = (g, logo) => {
    const group = g.querySelector('[data-adc-logo]');
    const emb = g.querySelector('[data-adc-emb]');
    if (!group) return;
    group.toggleAttribute('hidden', !logo);
    if (emb) emb.toggleAttribute('hidden', !!logo);
    const img = group.querySelector('[data-adc-logo-img]');
    const hasImg = !!(logo && logo.url);
    if (img) {
      img.toggleAttribute('hidden', !hasImg);
      if (hasImg) img.setAttribute('href', logo.url);
    }
    group.querySelectorAll('rect, text').forEach((el) => el.toggleAttribute('hidden', hasImg));
  };

  let logo = null; // { file, url, name }
  const state = {
    get name() { return store.get('adc_nom'); },
    get colorName() { return store.get('adc_coloris'); },
    get colorHex() { return store.get('adc_coloris_hex'); },
    get threadName() { return store.get('adc_fil'); },
    get threadHex() { return store.get('adc_fil_hex'); },
    get tenue() { return store.get('adc_tenue'); },
    get logo() { return logo; },
  };

  // Logo déposé : version d'affichage sans fond blanc, et repère « logo foncé » (pour un tissu foncé).
  // Le fichier d'origine reste celui qui est envoyé avec la commande ou la demande de maquette.
  const prepareLogo = (lg) => new Promise((resolve) => {
    if (!lg || !lg.url) { resolve(); return; }
    const isSvg = /svg/.test(lg.file && lg.file.type);
    const img = new Image();
    img.onload = () => {
      try {
        const nw = img.naturalWidth || 900, nh = img.naturalHeight || 900;
        const k = isSvg ? 900 / Math.max(nw, nh) : Math.min(1, 900 / Math.max(nw, nh));
        const w = Math.max(1, Math.round(nw * k)), h = Math.max(1, Math.round(nh * k));
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, w, h);
        const d = ctx.getImageData(0, 0, w, h);
        const px = d.data;
        const at = (x, y) => (y * w + x) * 4;
        const corners = [at(0, 0), at(w - 1, 0), at(0, h - 1), at(w - 1, h - 1)];
        const whiteBg = corners.every((i) => px[i + 3] > 240 && px[i] > 228 && px[i + 1] > 228 && px[i + 2] > 228);
        if (whiteBg) {
          // Seul le blanc relié aux bords est retiré : les blancs à l'intérieur du logo restent.
          const seen = new Uint8Array(w * h);
          const queue = [];
          const light = (n) => { const i = n * 4; return Math.min(px[i], px[i + 1], px[i + 2]) > 215; };
          const push = (n) => { if (!seen[n] && light(n)) { seen[n] = 1; queue.push(n); } };
          for (let x = 0; x < w; x++) { push(x); push((h - 1) * w + x); }
          for (let y = 0; y < h; y++) { push(y * w); push(y * w + w - 1); }
          while (queue.length) {
            const n = queue.pop();
            const i = n * 4;
            const m = Math.min(px[i], px[i + 1], px[i + 2]);
            px[i + 3] = m > 238 ? 0 : Math.round(px[i + 3] * (238 - m) / 23);
            const x = n % w, y = (n - x) / w;
            if (x > 0) push(n - 1);
            if (x < w - 1) push(n + 1);
            if (y > 0) push(n - w);
            if (y < h - 1) push(n + w);
          }
          ctx.putImageData(d, 0, 0);
        }
        let lum = 0, n = 0, clear = 0;
        for (let i = 0; i < px.length; i += 4) {
          if (px[i + 3] < 128) { clear += 1; continue; }
          lum += 0.2126 * px[i] + 0.7152 * px[i + 1] + 0.0722 * px[i + 2];
          n += 1;
        }
        // « Logo foncé » seulement s'il a une vraie transparence : un logo plein garde ses couleurs.
        lg.dark = n > 0 && clear / (w * h) >= 0.1 && lum / n / 255 < 0.35;
        if (!whiteBg) { resolve(); return; }
        c.toBlob((b) => {
          if (b && logo === lg) lg.view = URL.createObjectURL(b);
          resolve();
        }, 'image/png');
      } catch (e) { resolve(); }
    };
    img.onerror = () => {
      // Fichier illisible : pas d'aperçu, le cadre « VOTRE LOGO » et la mention prennent le relais.
      if (lg.url) URL.revokeObjectURL(lg.url);
      lg.url = '';
      resolve();
    };
    img.src = lg.url;
  });

  // Photo d'une tenue : change de photo selon le coloris, pose le logo (ou le nom) sur la zone de broderie.
  const paintStage = (st, s) => {
    const img = st.querySelector('[data-adc-mark-logo]');
    const mono = st.querySelector('[data-adc-mark-mono]');
    const txt = st.querySelector('[data-adc-mark-text]');
    if (!txt) return;
    if (s.colorName && st.dataset.color !== s.colorName) {
      if (!st._adcImages) {
        try { st._adcImages = JSON.parse(st.querySelector('[data-adc-stage-images]').textContent); } catch (e) { st._adcImages = {}; }
      }
      const im = st._adcImages[s.colorName];
      const photo = st.querySelector('.adc-stage__img');
      if (im && photo) {
        photo.srcset = im.srcset;
        photo.src = im.src;
        if (im.alt && photo.getAttribute('alt')) photo.alt = im.alt;
        st.dataset.color = s.colorName;
      }
    }
    const hex = s.colorHex || st.dataset.hex || '';
    const darkGarment = hex ? isDark(hex) : false;
    st.style.setProperty('--adc-mark-ink', darkGarment ? '#EEECE6' : '#1D1F22');
    st.style.setProperty('--adc-thread', s.threadHex || (darkGarment ? '#EEECE6' : '#1D1F22'));
    const lg = s.logo;
    const view = lg && (lg.view || lg.url);
    let mode = 'vide';
    // Logo foncé sur tissu très foncé (noir, bleu marine) : affiché d'une seule couleur claire, comme un fil clair.
    const [r0, g0, b0] = hexRgb(hex || '#ffffff');
    const veryDark = (0.2126 * r0 + 0.7152 * g0 + 0.0722 * b0) / 255 < 0.32;
    if (lg && view) mode = lg.dark && veryDark ? 'mono' : 'logo';
    else if (lg) mode = 'fichier';
    else if (s.name && s.name.trim()) mode = 'nom';
    st.dataset.mark = mode;
    img.hidden = mode !== 'logo';
    mono.hidden = mode !== 'mono';
    txt.hidden = mode === 'logo' || mode === 'mono';
    if (mode === 'logo' && img.getAttribute('src') !== view) img.src = view;
    if (mode === 'mono') mono.style.setProperty('--logo', `url("${view}")`);
    // Annonce pour les lecteurs d'écran, sans le nom saisi ni le nom du fichier.
    const sr = st.querySelector('[data-adc-mark-sr]');
    if (sr) sr.textContent = mode === 'logo' || mode === 'mono' ? `Simulation avec votre logo, ${st.dataset.place || ''}.` : (mode === 'nom' ? `Simulation du nom brodé, ${st.dataset.place || ''}.` : '');
    if (mode === 'nom') {
      const [l1, l2] = splitName(s.name.trim());
      const longest = Math.max(l1.length, l2.length, 1);
      const w = parseFloat(st.style.getPropertyValue('--w')) || 12;
      txt.textContent = l2 ? `${l1}\n${l2}` : l1;
      txt.style.fontSize = `${Math.min(w * 0.2, w / (0.62 * longest))}cqw`;
    } else {
      txt.textContent = 'VOTRE LOGO';
      txt.style.fontSize = '';
    }
  };

  // Valeur d'un champ du formulaire. L'emplacement et le type de broderie mémorisés ne valent que sur une
  // fiche produit (là où le visiteur les a choisis) ; ailleurs, la valeur de la section fait foi.
  const fieldValue = (f) => {
    const onProduct = !!document.querySelector('adc-configurator');
    switch (f.dataset.adcLiveField) {
      case 'nom': return state.name;
      case 'coloris': return state.colorName;
      case 'fil': return state.threadName;
      case 'emplacement': return (onProduct && store.get('adc_emplacement')) || f.defaultValue;
      case 'broderie': return logo ? (state.name ? 'Logo et nom' : 'Logo') : 'Texte';
      default: return f.value;
    }
  };

  // Tous les dessins marqués data-adc-live suivent le nom et le logo ; le coloris et le fil s'ils l'acceptent.
  const repaint = (root) => {
    (root || document).querySelectorAll('[data-adc-live] [data-adc-stage], [data-adc-stage][data-adc-live]').forEach((st) => {
      const host = st.closest('[data-adc-live]');
      const style = !!(host && host.dataset.adcLive === 'style');
      paintStage(st, { name: state.name, logo, colorName: style ? state.colorName : '', colorHex: style ? state.colorHex : '', threadHex: style ? state.threadHex : '' });
    });
    (root || document).querySelectorAll('[data-adc-live] [data-adc-garment], [data-adc-garment][data-adc-live]').forEach((g) => {
      paintName(g, state.name);
      paintLogo(g, logo);
      const host = g.closest('[data-adc-live]');
      if (host && host.dataset.adcLive === 'style') { paintColor(g, state.colorHex); paintThread(g, state.threadHex); }
    });
    document.querySelectorAll('[data-adc-live-field]').forEach((f) => {
      if (document.activeElement !== f) f.value = fieldValue(f);
    });
    document.querySelectorAll('[data-adc-live-text]').forEach((el) => {
      el.textContent = state.name || el.dataset.adcLiveText || PLACEHOLDER;
    });
    // État du logo : nom du fichier, bouton « Retirer », nom à broder facultatif quand un logo est joint
    document.querySelectorAll('[data-adc-logo-status]').forEach((el) => { el.hidden = !logo; });
    document.querySelectorAll('[data-adc-logo-name]').forEach((el) => { el.textContent = logo ? logo.name : ''; });
    document.querySelectorAll('[data-adc-logo-label]').forEach((el) => { el.textContent = logo ? (el.dataset.adcLogoLabelOn || 'Changer de fichier') : (el.dataset.adcLogoLabel || 'Joindre un fichier'); });
    document.querySelectorAll('[data-adc-visual-form] [data-adc-live-field="nom"]').forEach((f) => { f.required = !logo; });
  };

  const setName = (raw) => {
    const v = clean(raw);
    if (v === state.name) return v;
    store.set('adc_nom', v);
    repaint();
    document.dispatchEvent(new CustomEvent('adc:nom', { detail: { length: v.length } }));
    return v;
  };
  const setStyle = (o) => {
    if (o.colorName != null) { store.set('adc_coloris', o.colorName); store.set('adc_coloris_hex', o.colorHex || ''); }
    if (o.threadName != null) { store.set('adc_fil', o.threadName); store.set('adc_fil_hex', o.threadHex || ''); }
    if (o.placement != null) store.set('adc_emplacement', o.placement);
    if (o.tenue != null) store.set('adc_tenue', o.tenue);
    repaint();
    document.dispatchEvent(new CustomEvent('adc:style'));
  };
  // Renvoie un message d'erreur si le fichier est refusé, sinon une chaîne vide.
  const setLogo = (file, source) => {
    if (file && file.size > LOGO_MAX) return 'Ce fichier dépasse 10 Mo. Choisissez un fichier plus léger.';
    if (logo && logo.url) URL.revokeObjectURL(logo.url);
    if (logo && logo.view) URL.revokeObjectURL(logo.view);
    logo = file ? { file, name: file.name, url: /^image\/(png|jpe?g|gif|webp|svg\+xml)$/.test(file.type) ? URL.createObjectURL(file) : '' } : null;
    if (logo && logo.url) {
      const lg = logo;
      prepareLogo(lg).then(() => {
        repaint();
        if (logo === lg && !lg.url) document.dispatchEvent(new CustomEvent('adc:logo', { detail: { source: 'illisible' } }));
      });
    }
    if (!file) document.querySelectorAll('[data-adc-logo-input]').forEach((i) => { i.value = ''; });
    document.querySelectorAll('[data-adc-logo-url], [data-adc-logo-filename]').forEach((i) => { i.value = ''; });
    document.querySelectorAll('[data-adc-logo-live]').forEach((el) => { el.textContent = logo ? 'Logo joint' : (source === 'retrait' ? 'Logo retiré' : ''); });
    repaint();
    document.dispatchEvent(new CustomEvent('adc:logo', { detail: { source: source || null } }));
    return '';
  };

  // Téléversement du logo sans app : Shopify stocke les fichiers joints à une ligne de panier.
  // Le fichier est ajouté à une ligne temporaire, son adresse est récupérée, puis la ligne est retirée.
  const uploadLogo = async (file, variantId) => {
    const base = (window.routes && window.routes.cart_add_url) || '/cart/add';
    const fd = new FormData();
    fd.append('id', variantId);
    fd.append('quantity', 1);
    fd.append('properties[_Envoi du logo]', String(file.size) + '-' + String(file.lastModified || 0));
    fd.append('properties[Logo]', file, file.name);
    const r = await fetch(base + '.js', { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
    const j = await r.json();
    if (!r.ok) throw new Error(j.description || j.message || 'Téléversement impossible');
    const item = j.items ? j.items[0] : j;
    const url = item && item.properties ? item.properties.Logo : '';
    try {
      const change = (window.routes && window.routes.cart_change_url) || '/cart/change';
      await fetch(change + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: item.key, quantity: 0 }) });
    } catch (e) {}
    if (!url) throw new Error('Adresse du fichier absente');
    return url;
  };

  // Pastilles des cartes de tenues : la photo de la carte change de coloris ; en ouvrant la fiche, ce coloris est repris.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-adc-card-color]');
    if (btn) {
      const card = btn.closest('[data-adc-card]');
      const st = card && card.querySelector('.adc-stage');
      card.querySelectorAll('[data-adc-card-color]').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
      card.dataset.color = btn.dataset.adcCardColor;
      card.dataset.hex = btn.dataset.hex || '';
      if (st) {
        if (!st._adcImages) {
          try { st._adcImages = JSON.parse(st.querySelector('[data-adc-stage-images]').textContent); } catch (err) { st._adcImages = {}; }
        }
        const im = st._adcImages[btn.dataset.adcCardColor];
        const photo = st.querySelector('.adc-stage__img');
        if (im && photo) { photo.srcset = im.srcset; photo.src = im.src; }
        st.dataset.color = btn.dataset.adcCardColor;
        if (btn.dataset.hex) st.dataset.hex = btn.dataset.hex;
        repaint();
      }
      return;
    }
    const link = e.target.closest('[data-adc-card-link]');
    const card = link && link.closest('[data-adc-card]');
    if (card && card.dataset.color) {
      store.set('adc_coloris', card.dataset.color);
      store.set('adc_coloris_hex', card.dataset.hex || '');
    }
  });

  window.adcGarment = { cfg, PLACEHOLDER, clean, writeBack, splitName, paintName, paintColor, paintThread, paintLogo, paintStage, mix, isDark, state, setName, setStyle, setLogo, uploadLogo, repaint, fromLink: () => store.get('adc_nom_source') === 'lien' };

  const track = (name, data, detail) => { if (typeof window.adcTrack === 'function') window.adcTrack(name, data || {}, detail); };
  const tag = (key, value) => { if (typeof window.adcTag === 'function') window.adcTag(key, value); };

  const init = () => {
    repaint();
    if (window.adcNomArrivee) { track('arrivee_lien'); window.adcNomArrivee = false; }
    // Champs « Nom à broder » hors configurateur (formulaire)
    document.querySelectorAll('[data-adc-live-field="nom"]').forEach((f) => {
      if (f.type === 'hidden') return;
      f.addEventListener('input', () => writeBack(f, setName(f.value)));
    });
    // Logo : champ fichier du formulaire, boutons « joindre » et « retirer » ailleurs sur la page
    document.querySelectorAll('[data-adc-logo-input]').forEach((input) => {
      input.addEventListener('change', () => {
        const err = setLogo(input.files && input.files[0] ? input.files[0] : null, 'formulaire');
        const box = input.closest('[data-adc-logo-field]');
        const msg = box && box.querySelector('[data-adc-logo-error]');
        if (msg) msg.textContent = err;
        if (err) { input.value = ''; track('logo_refuse', {}, 'poids'); }
        else if (input.files && input.files[0]) { track('logo_joint'); tag('logo', 'joint'); }
      });
    });
    document.addEventListener('click', (e) => {
      const pick = e.target.closest('[data-adc-logo-pick]');
      if (pick) { const input = document.querySelector('[data-adc-logo-input]'); if (input) input.click(); return; }
      const clear = e.target.closest('[data-adc-logo-clear]');
      if (clear) {
        // Le bouton « Retirer » disparaît avec la ligne : le focus revient sur le bouton ou le champ du logo.
        const box = clear.closest('[data-adc-logo-field]');
        setLogo(null, 'retrait');
        const next = box && box.querySelector('[data-adc-logo-pick]:not([hidden]), [data-adc-logo-input]:not([hidden])');
        if (next) next.focus();
        return;
      }
      // Clics sur les appels à l'action, sans aucune donnée saisie
      const a = e.target.closest('[data-adc-cta]');
      if (a) track('cta_clic', { emplacement: a.dataset.adcCta, chemin: a.dataset.adcChemin || '' }, a.dataset.adcCta + '_' + (a.dataset.adcChemin || ''));
    });
    if (!document.querySelector('[data-adc-logo-input]')) document.querySelectorAll('[data-adc-logo-pick]').forEach((b) => { b.hidden = true; });

    // Provenance de la demande (page publicité) : source et campagne de l'adresse, gardées pour la visite.
    const sourceFields = document.querySelectorAll('[data-adc-source]');
    if (sourceFields.length) {
      let src = '';
      try {
        const q = new URLSearchParams(location.search);
        const clean = (v) => String(v || '').replace(/[^\w .-]/g, '').slice(0, 40);
        const parts = [clean(q.get('utm_source')), clean(q.get('utm_campaign')), clean(q.get('utm_content'))].filter(Boolean);
        if (!parts.length && q.get('ttclid')) parts.push('tiktok');
        src = parts.join(' / ');
        if (src) sessionStorage.setItem('adc_source', src); else src = sessionStorage.getItem('adc_source') || '';
      } catch (err) {}
      sourceFields.forEach((f) => { f.value = src || 'Direct'; });
    }

    // Envoi des demandes de maquette. L'écouteur est posé sur le document, en phase de capture : il passe
    // avant le contrôle anti-robot de Shopify (hCaptcha), qui envoie le formulaire dès qu'il a sa réponse,
    // et peut ainsi retenir l'envoi le temps de téléverser le logo.
    document.addEventListener('submit', async (e) => {
      const form = e.target && e.target.matches && e.target.matches('[data-adc-visual-form]') ? e.target : null;
      if (!form) return;
      const hold = () => { e.preventDefault(); e.stopImmediatePropagation(); };
      // Un téléversement est déjà en cours : le second envoi (double clic, touche Entrée) est ignoré.
      if (form.dataset.adcBusy === '1') { hold(); return; }
      repaint();
      const urlField = form.querySelector('[data-adc-logo-url]');
      const nameField = form.querySelector('[data-adc-logo-filename]');
      // Sans logo, aucune adresse ni nom de fichier d'un envoi précédent ne part.
      if (!logo) { if (urlField) urlField.value = ''; if (nameField) nameField.value = ''; }
      if (!logo || !urlField || urlField.value) {
        // Demande de devis de la page publicité : nombre de tenues cochées, jamais le texte saisi.
        if (form.dataset.adcFormKind) {
          track(form.dataset.adcFormKind + '_demande', { tenues: form.querySelectorAll('[data-adc-tenue]:checked').length, logo: logo ? 'oui' : 'non' });
          return;
        }
        const sel = form.querySelector('select, input[name="contact[Tenue]"]:checked');
        track('visuel_demande', { tenue: sel ? sel.value : '', logo: logo ? 'oui' : 'non' }, sel ? sel.value : '');
        if (sel) tag('tenue', sel.value);
        return;
      }
      // Premier passage avec un logo : on le téléverse, puis on renvoie le formulaire normalement.
      hold();
      form.dataset.adcBusy = '1';
      const sent = logo;
      const btn = form.querySelector('[type="submit"]');
      const label = btn ? btn.textContent : '';
      if (btn) { btn.setAttribute('aria-disabled', 'true'); btn.textContent = 'Envoi du logo…'; }
      let url;
      try {
        url = await uploadLogo(sent.file, form.dataset.uploadVariant);
      } catch (err) {
        url = 'Fichier non transmis (à redemander au client)';
        track('logo_envoi_echec');
      }
      delete form.dataset.adcBusy;
      if (btn) { btn.removeAttribute('aria-disabled'); btn.textContent = label; }
      // Logo retiré ou remplacé pendant l'envoi : rien ne part, le visiteur renvoie avec le bon fichier.
      if (logo !== sent) return;
      urlField.value = url;
      if (nameField) nameField.value = sent.name;
      if (form.requestSubmit) form.requestSubmit(btn || undefined); else form.submit();
    }, true);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  // Retour arrière du navigateur : la page restaurée reprend le dernier nom, coloris et fil.
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    document.querySelectorAll('[data-adc-visual-form]').forEach((f) => { delete f.dataset.adcBusy; });
    repaint();
    document.dispatchEvent(new CustomEvent('adc:nom', { detail: { length: state.name.length } }));
    document.dispatchEvent(new CustomEvent('adc:style'));
  });
})();
